import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Backdrop, Box, Button, CircularProgress, Fade, Modal, Stack, Typography } from '@mui/material';
import { registerChallengeHandler, unregisterChallengeHandler } from '../network/turnstileChallenge';

const TURNSTILE_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let scriptPromise = null;

const loadTurnstileScript = () => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('window is not available'));
  }
  if (window.turnstile) {
    return Promise.resolve(window.turnstile);
  }
  if (scriptPromise) {
    return scriptPromise;
  }
  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = TURNSTILE_URL;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.turnstile) {
        resolve(window.turnstile);
      } else {
        reject(new Error('Turnstile script loaded but window.turnstile is undefined'));
      }
    };
    script.onerror = () => {
      scriptPromise = null;
      reject(new Error('Failed to load Cloudflare Turnstile'));
    };
    document.body.appendChild(script);
  });
  return scriptPromise;
};

const modalStyles = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 360,
  maxWidth: '90vw',
  backgroundColor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  maxHeight: '90vh',
  overflowY: 'auto',
  p: 3,
};

export default function TurnstileChallengeProvider({ children }) {
  const [challenge, setChallenge] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const widgetContainerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const queueRef = useRef([]);

  const cleanupWidget = useCallback(() => {
    if (widgetIdRef.current && window.turnstile) {
      if (typeof window.turnstile.remove === 'function') {
        window.turnstile.remove(widgetIdRef.current);
      } else if (typeof window.turnstile.reset === 'function') {
        window.turnstile.reset(widgetIdRef.current);
      }
    }
    widgetIdRef.current = null;
    if (widgetContainerRef.current) {
      widgetContainerRef.current.innerHTML = '';
    }
  }, []);

  const shiftQueue = useCallback(() => {
    setChallenge(queueRef.current.shift() ?? null);
  }, []);

  const resolveChallenge = useCallback(
    (value) => {
      if (challenge?.resolve) {
        challenge.resolve(value);
      }
      setErrorMessage('');
      cleanupWidget();
      shiftQueue();
    },
    [challenge, cleanupWidget, shiftQueue],
  );

  const rejectChallenge = useCallback(
    (err) => {
      if (challenge?.reject) {
        challenge.reject(err);
      }
      setErrorMessage('');
      cleanupWidget();
      shiftQueue();
    },
    [challenge, cleanupWidget, shiftQueue],
  );

  const handleCancel = useCallback(() => {
    resolveChallenge(null);
  }, [resolveChallenge]);

  const resetWidget = useCallback(() => {
    if (widgetIdRef.current && window.turnstile?.reset) {
      window.turnstile.reset(widgetIdRef.current);
      setErrorMessage('');
    }
  }, []);

  useEffect(() => {
    const handler = ({ siteKey, retryAfter }) =>
      new Promise((resolve, reject) => {
        if (!siteKey) {
          resolve(null);
          return;
        }
        queueRef.current.push({ siteKey, retryAfter, resolve, reject });
        setChallenge((current) => current ?? queueRef.current.shift() ?? null);
      });

    registerChallengeHandler(handler);
    return () => unregisterChallengeHandler(handler);
  }, []);

  useEffect(() => {
    if (!challenge) {
      cleanupWidget();
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setErrorMessage('');

    loadTurnstileScript()
      .then((turnstile) => {
        if (cancelled || !widgetContainerRef.current) {
          return;
        }

        widgetContainerRef.current.innerHTML = '';
        widgetIdRef.current = turnstile.render(widgetContainerRef.current, {
          sitekey: challenge.siteKey,
          callback: (token) => {
            resolveChallenge(token);
          },
          'error-callback': () => {
            setErrorMessage('Verification failed, please try again.');
            resetWidget();
          },
          'expired-callback': () => {
            setErrorMessage('Challenge expired, click retry to refresh.');
            resetWidget();
          },
        });
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) {
          return;
        }
        setLoading(false);
        rejectChallenge(err);
      });

    return () => {
      cancelled = true;
      cleanupWidget();
    };
  }, [challenge, cleanupWidget, rejectChallenge, resolveChallenge, resetWidget]);

  return (
    <>
      {children}
      <Modal
        open={Boolean(challenge)}
        onClose={handleCancel}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 200,
          },
        }}
      >
        <Fade in={Boolean(challenge)}>
          <Box sx={modalStyles}>
            <Typography variant="h6" gutterBottom>
              Extra verification required
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please complete the CAPTCHA challenge to continue.
              {challenge?.retryAfter ? ` You can retry in about ${challenge.retryAfter} seconds if needed.` : ''}
            </Typography>
            <Box sx={{ mt: 3, minHeight: 80 }} ref={widgetContainerRef} />
            {loading && (
              <Stack alignItems="center" sx={{ py: 2 }}>
                <CircularProgress size={24} />
              </Stack>
            )}
            {errorMessage && (
              <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                {errorMessage}
              </Typography>
            )}
            <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 3 }}>
              <Button onClick={resetWidget} disabled={loading}>
                Retry
              </Button>
              <Button onClick={handleCancel} color="inherit">
                Cancel
              </Button>
            </Stack>
          </Box>
        </Fade>
      </Modal>
    </>
  );
}

TurnstileChallengeProvider.propTypes = {
  children: PropTypes.node,
};
